/**
 * PullToRefresh - Native-style pull-to-refresh wrapper for touch devices.
 *
 * Wraps a scrollable region. When the user is scrolled to the top
 * (scrollTop === 0) and drags downward past a threshold, the awaited
 * `onRefresh` callback runs and a spinner is shown until it resolves.
 *
 * - Touch-only: mouse/desktop is a harmless no-op (no listeners fire).
 * - Resistance is applied to the drag so the pull feels weighted.
 * - Respects prefers-reduced-motion (snap, no easing transition).
 *
 * Reference: Issue #333 (Phase 1 - base component + AppHomePage wiring)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { OroLoader } from '../OroLoader';
import { getInitialReducedMotion } from '../../utils/motionPreferences';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  /**
   * Px to push the pull indicator down so it clears a `position: fixed` header
   * overlapping the top of the scroll area (e.g. the app TopBar, h-14 = 56px).
   * The indicator is positioned from this wrapper's top (viewport y=0), which
   * sits *under* the fixed TopBar — without this offset the in-flight spinner
   * is hidden behind it (#1992). Default 0 (no fixed header).
   */
  topOffset?: number;
}

// Distance (px) the user must pull past before a release triggers a refresh.
const TRIGGER_THRESHOLD = 72;
// Pull distance is divided by this so the gesture feels weighted (resistance).
const RESISTANCE = 2.5;
// Cap on how far the indicator can travel while dragging.
const MAX_PULL = 120;
// Resting offset (px) the indicator holds at while the refresh is in flight.
const REFRESHING_OFFSET = 56;

// Walk up from `start` (inclusive) to the nearest scrollable ancestor that
// actually overflows; fall back to the document scroller for page-level scroll.
// PullToRefresh is frequently mounted INSIDE an existing scroll container (e.g.
// Layout's `<main className="... overflow-y-auto">`), in which case our own
// wrapper never scrolls and its scrollTop is always 0 — gating on it would
// hijack normal mid-page scrolling (#1992). So gate on the real scroller.
function resolveScrollEl(start: HTMLElement | null): HTMLElement {
  let el: HTMLElement | null = start;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

export default function PullToRefresh({ children, onRefresh, topOffset = 0 }: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const isRefreshingRef = useRef(false);
  // The element that ACTUALLY scrolls the content, resolved per-gesture on
  // touchstart (see resolveScrollEl) — not necessarily our own wrapper.
  const scrollElRef = useRef<HTMLElement | null>(null);
  // Latest pull distance mirrored to a ref. touchmove writes React state, but
  // touchend must read the up-to-date value synchronously: native touch
  // listeners + React batching can otherwise leave the state closure stale on a
  // fast move→release, missing a legitimate trigger (#1992).
  const pullDistanceRef = useRef(0);

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // isPulling drives the settle-animation toggle in render. While the finger
  // is down we follow it 1:1 (no transition); on release we animate the snap.
  // Mirrored to pullingRef so the once-bound touch handlers stay in sync.
  const [isPulling, setIsPulling] = useState(false);
  const reducedMotion = getInitialReducedMotion();

  // Keep a ref mirror of isRefreshing so touch handlers (bound once) can read
  // the latest value without re-binding listeners on every state change.
  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (isRefreshingRef.current) return;
    // Resolve the real scroll container for this gesture, then only arm when
    // it's at the very top so normal mid-content scrolling is never hijacked.
    const scrollEl = (scrollElRef.current = resolveScrollEl(containerRef.current));
    if (scrollEl.scrollTop <= 0) {
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
      setIsPulling(true);
    } else {
      startYRef.current = null;
      pullingRef.current = false;
      setIsPulling(false);
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!pullingRef.current || startYRef.current === null || isRefreshingRef.current) return;

    const delta = e.touches[0].clientY - startYRef.current;

    // Upward / non-pull movement: disengage and let the browser scroll.
    if (delta <= 0) {
      if (pullDistanceRef.current !== 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
      pullingRef.current = false;
      setIsPulling(false);
      return;
    }

    // If the user has scrolled away from the top mid-gesture, abort.
    if ((scrollElRef.current?.scrollTop ?? 0) > 0) {
      pullingRef.current = false;
      setIsPulling(false);
      pullDistanceRef.current = 0;
      setPullDistance(0);
      return;
    }

    const resisted = Math.min(delta / RESISTANCE, MAX_PULL);
    pullDistanceRef.current = resisted;
    setPullDistance(resisted);

    // Prevent the native overscroll/bounce while we own the gesture.
    if (e.cancelable) e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!pullingRef.current || isRefreshingRef.current) {
      startYRef.current = null;
      pullingRef.current = false;
      setIsPulling(false);
      return;
    }

    pullingRef.current = false;
    setIsPulling(false);
    startYRef.current = null;

    // Read the mirrored ref, not the (possibly stale) state closure.
    if (pullDistanceRef.current >= TRIGGER_THRESHOLD) {
      setIsRefreshing(true);
      pullDistanceRef.current = REFRESHING_OFFSET;
      setPullDistance(REFRESHING_OFFSET);
      void Promise.resolve(onRefresh())
        .catch(() => {
          // Swallow here: the underlying query surfaces its own error state.
          // We only own the gesture/spinner lifecycle.
        })
        .finally(() => {
          setIsRefreshing(false);
          pullDistanceRef.current = 0;
          setPullDistance(0);
        });
    } else {
      // Not enough pull — snap back.
      pullDistanceRef.current = 0;
      setPullDistance(0);
    }
  }, [onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // touchmove must be non-passive so preventDefault() can suppress overscroll.
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const triggered = pullDistance >= TRIGGER_THRESHOLD;
  // Rotate the icon proportionally to the pull before the spinner takes over.
  const indicatorRotation = isRefreshing ? 0 : Math.min((pullDistance / TRIGGER_THRESHOLD) * 270, 270);
  const indicatorOpacity = isRefreshing ? 1 : Math.min(pullDistance / TRIGGER_THRESHOLD, 1);
  // While dragging we follow the finger 1:1 (no transition). On release/refresh
  // settle, animate the snap unless the user prefers reduced motion.
  const animateSettle = !isPulling && !reducedMotion;

  return (
    <div className="relative flex flex-1 flex-col min-h-0">
      {/* Pull indicator: lives above the content, revealed as it slides down. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center"
        style={{
          transform: `translateY(${pullDistance - 40 + topOffset}px)`,
          transition: animateSettle ? 'transform 200ms ease-out' : 'none',
          opacity: indicatorOpacity,
        }}
        aria-hidden={!isRefreshing}
      >
        <div className="mt-2 flex h-11 w-11 items-center justify-center rounded-full bg-oro-surface border border-oro-border shadow-card">
          {isRefreshing ? (
            // Refreshing: the branded loader (#2690). It owns its own
            // reduced-motion degradation, replacing the former spinner.
            <OroLoader variant="bounce" size={28} label="Refreshing" />
          ) : (
            // Pulling: the icon rotates 1:1 with the drag distance.
            <Loader2
              className={`h-5 w-5 ${triggered ? 'text-accent' : 'text-icon'}`}
              style={{
                transform: `rotate(${indicatorRotation}deg)`,
                transition: animateSettle ? 'transform 200ms ease-out' : 'none',
              }}
            />
          )}
        </div>
      </div>

      {/* Scrollable content. The wrapper owns the scroll + gesture. */}
      <div
        ref={containerRef}
        className="flex flex-1 flex-col min-h-0 overflow-y-auto"
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: animateSettle ? 'transform 200ms ease-out' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
